import { useEffect } from 'react'

import { faArrowToBottom, faLock } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge, Button, CircularProgress, IconButton, useMediaQuery, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@scoir/tooltips'
import downloadFile from 'js-file-download'

import { createAPIFiltersFromUIFilters } from '../filter-state-to-api-query'
import { ExtendedGridColDef } from './columns'
import { Class, HistoricFollowerYear } from './gradYears'
import { ResultOptions, UIFilters } from './types'
import useExportHighschoolsExplore from './useExportHighschoolsExplore'

export default function ExportButton({
  resultOptions,
  filters,
  columns,
  enabled,
  selectedHandraiserClasses,
  selectedFollowerClasses,
  selectedHistoricFollowerYears,
}: {
  resultOptions: ResultOptions
  filters: UIFilters
  columns: ExtendedGridColDef[]
  enabled: boolean
  selectedHandraiserClasses?: Class[]
  selectedFollowerClasses?: Class[]
  selectedHistoricFollowerYears?: HistoricFollowerYear[]
}) {
  const { mutate, isSuccess, isLoading, data, reset } = useExportHighschoolsExplore()
  useEffect(() => {
    if (isSuccess && data) {
      const date = Intl.DateTimeFormat('en-US').format(new Date()).replace(/\//g, '-')
      const fileName = `high-schools-export-${date}.csv`
      downloadFile(data, fileName, 'text/csv')
      reset()
    }
  }, [isSuccess, data, reset])

  const handleExport = () => {
    mutate({
      resultOptions: resultOptions,
      filters: createAPIFiltersFromUIFilters(filters, {
        handraiserClasses: selectedHandraiserClasses ? [...selectedHandraiserClasses] : undefined,
        followerClasses: selectedFollowerClasses ? [...selectedFollowerClasses] : undefined,
        historicFollowerYears: selectedHistoricFollowerYears ? [...selectedHistoricFollowerYears] : undefined,
      }),
      columns: columns
        .filter((c) => !c.skipExport)
        .map((column) => ({
          field: column.field || '',
          heading: column.headerName || '',
        })),
      selectedHandraiserClasses,
      selectedFollowerClasses,
      selectedHistoricFollowerYears,
    })
  }

  return !enabled ? (
    <Badge
      color="secondary"
      badgeContent={<FontAwesomeIcon icon={faLock} />}
    >
      <ResponsiveExportButton disabled />
    </Badge>
  ) : (
    <ResponsiveExportButton
      isLoading={isLoading}
      handleExport={handleExport}
    />
  )
}

const useStyles = makeStyles((theme) => ({
  iconButton: {
    '&.MuiIconButton-root.Mui-disabled': {
      color: theme.palette.primary.main,
      opacity: 0.3,
    },
  },
}))
type ResponsiveExportButtonProps = {
  disabled?: boolean
  isLoading?: boolean
  handleExport?: () => void
}
const ResponsiveExportButton = ({ disabled, isLoading, handleExport }: ResponsiveExportButtonProps) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('md'))
  return isMediumScreenSize ? (
    <Tooltip title="Export">
      <IconButton
        color="primary"
        className={classes.iconButton}
        disabled={disabled}
        onClick={() => handleExport?.()}
      >
        {isLoading ? (
          <div>
            <CircularProgress size={16} />
          </div>
        ) : (
          <FontAwesomeIcon icon={faArrowToBottom} />
        )}
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      color="primary"
      disabled={isLoading || disabled}
      onClick={() => handleExport?.()}
      startIcon={
        isLoading ? (
          <div>
            <CircularProgress size={16} />
          </div>
        ) : (
          <FontAwesomeIcon icon={faArrowToBottom} />
        )
      }
    >
      Export
    </Button>
  )
}
